import { useEffect, useState } from "react";
import { CalendarDays, CreditCard, Download, Plus, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { BookingWizard } from "@/components/booking/BookingWizard";
import { AppointmentActions } from "@/components/booking/AppointmentActions";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { downloadFile, request } from "@/lib/api";
import { loadRazorpayCheckout } from "@/lib/razorpay";

export default function HealthChecksPage() {
  const { token } = useAuth();
  const { name: tenantName } = useTenant();
  const [showWizard, setShowWizard] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [labOrders, setLabOrders] = useState([]);
  const [statusTab, setStatusTab] = useState("confirmed");
  const [confirmations, setConfirmations] = useState([]);
  const [appointmentLetter, setAppointmentLetter] = useState(null);
  const [paymentState, setPaymentState] = useState({});

  useEffect(() => {
    request("/bookings", {}, token).then(setBookings).catch(() => setBookings([]));
    // Lab orders are bookings the employee made and had nowhere to look at
    // afterwards — the catalogue could take an order and then lose sight of it.
    request("/lab-tests/orders", {}, token).then(setLabOrders).catch(() => setLabOrders([]));
  }, [token]);

  function completeBookings(created) {
    setConfirmations(created);
    setBookings((current) => [...created, ...current]);
    setShowWizard(false);
  }

  async function openLetter(bookingId) {
    const letter = await request(`/bookings/${bookingId}/appointment-letter`, {}, token);
    setAppointmentLetter(letter);
  }

  function downloadLetter(bookingId) {
    downloadFile(`/bookings/${bookingId}/appointment-letter/download`, `appointment-letter-${bookingId}.pdf`, token);
  }

  function recordChangeRequest(changeRequest) {
    setBookings((current) => current.map((booking) => booking.booking_id === changeRequest.booking_id ? { ...booking, self_service_status: "change_requested" } : booking));
  }

  function updateBookingEverywhere(bookingId, patch) {
    setConfirmations((current) => current.map((booking) => booking.booking_id === bookingId ? { ...booking, ...patch } : booking));
    setBookings((current) => current.map((booking) => booking.booking_id === bookingId ? { ...booking, ...patch } : booking));
  }

  async function startPayment(booking) {
    setPaymentState((current) => ({ ...current, [booking.booking_id]: { isOpening: true, error: "" } }));
    try {
      const order = await request(`/payments/bookings/${booking.booking_id}/order`, { method: "POST" }, token);
      const scriptLoaded = await loadRazorpayCheckout();
      if (!scriptLoaded || !window.Razorpay) {
        throw new Error("Secure payment could not be opened. Please try again.");
      }
      const checkout = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: order.name,
        description: order.description,
        order_id: order.order_id,
        prefill: order.prefill,
        theme: { color: "#166534" },
        handler: async (response) => {
          try {
            const result = await request(
              `/payments/bookings/${booking.booking_id}/verify`,
              { method: "POST", body: JSON.stringify(response) },
              token,
            );
            updateBookingEverywhere(booking.booking_id, result);
            setPaymentState((current) => ({ ...current, [booking.booking_id]: { isOpening: false, error: "" } }));
          } catch (verificationError) {
            setPaymentState((current) => ({ ...current, [booking.booking_id]: { isOpening: false, error: verificationError.message } }));
          }
        },
        modal: {
          ondismiss: () => setPaymentState((current) => ({ ...current, [booking.booking_id]: { isOpening: false, error: "" } })),
        },
      });
      checkout.open();
    } catch (paymentStartError) {
      setPaymentState((current) => ({ ...current, [booking.booking_id]: { isOpening: false, error: paymentStartError.message } }));
    }
  }

  const pendingPayments = confirmations.filter((booking) => booking.payment_status === "awaiting_payment");
  const sponsoredConfirmations = confirmations.filter((booking) => booking.payment_status !== "awaiting_payment");

  // One list, so nothing an employee booked can hide between two of them.
  const appointments = [
    ...bookings.map((booking) => ({
      kind: "booking",
      id: booking.booking_id,
      status: booking.self_service_status === "change_requested" ? "change_requested" : booking.status,
      date: booking.appointment_date,
      record: booking,
    })),
    ...labOrders.map((order) => ({
      kind: "lab_order",
      id: order.order_id,
      status: order.status,
      date: order.collection_date,
      record: order,
    })),
  ].sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  // Confirmed is the complement of the other two rather than a list of status
  // names: statuses get added over time, and a new one must show up somewhere
  // instead of silently belonging to no tab.
  const TABS = [
    { value: "confirmed", label: "Confirmed", matches: (item) => item.status !== "awaiting_payment" && item.status !== "cancelled" },
    { value: "awaiting_payment", label: "Awaiting payment", matches: (item) => item.status === "awaiting_payment" },
    { value: "cancelled", label: "Cancelled", matches: (item) => item.status === "cancelled" },
    { value: "all", label: "All", matches: () => true },
  ];
  const activeTab = TABS.find((tab) => tab.value === statusTab) || TABS[0];
  const visibleAppointments = appointments.filter(activeTab.matches);

  return (
    <section className="page-stack" data-testid="health-checks-page">
      <div className="page-heading">
        <p className="eyebrow">Health checks</p>
        <h1>Preventive care, your way.</h1>
        <p>While your health checkup is covered by {tenantName}, you may add your spouse, parent, child (18+), or other dependent with their details and complete payment to book their appointments.</p>
      </div>
      {sponsoredConfirmations.length > 0 && <div className="success-banner" data-testid="booking-success-message"><CalendarDays size={20} /><div><strong>Request received</strong><span>{sponsoredConfirmations.map((booking) => booking.booking_id).join(", ")} · Your appointment letter{sponsoredConfirmations.length > 1 ? "s" : ""} will be available in the portal and also via email.</span></div></div>}
      {pendingPayments.map((booking) => {
        const state = paymentState[booking.booking_id] || { isOpening: false, error: "" };
        return <section className="payment-panel" key={booking.booking_id} data-testid={`razorpay-payment-panel-${booking.booking_id}`}>
          <div><div className="payment-panel__icon"><CreditCard size={22} /></div><p className="eyebrow">Family-member health check</p><h2>Pay, then book</h2><p>Pay <span aria-label="Indian Rupees" data-testid={`razorpay-payment-amount-${booking.booking_id}`}>₹{booking.amount.toLocaleString("en-IN")}</span> securely to book this health check for {booking.beneficiary_name}.</p><span className="payment-assurance"><ShieldCheck size={16} />Razorpay secure checkout</span></div>
          <Button onClick={() => startPayment(booking)} disabled={state.isOpening} data-testid={`razorpay-pay-now-button-${booking.booking_id}`}>{state.isOpening ? "Opening secure payment…" : `Pay ₹${booking.amount.toLocaleString("en-IN")}`}</Button>
          {state.error && <p className="form-error" role="alert" data-testid={`razorpay-payment-error-${booking.booking_id}`}>{state.error}</p>}
        </section>;
      })}
      {confirmations.some((booking) => booking.payment_status === "paid") && <p className="success-inline" data-testid="razorpay-payment-success"><ShieldCheck size={18} />Payment received. Your provider confirmation is being prepared.</p>}
      {appointmentLetter && <section className="appointment-letter" data-testid="appointment-letter-panel"><div><p className="eyebrow">Appointment letter</p><h2>{appointmentLetter.title}</h2><p><strong>{appointmentLetter.beneficiary}</strong> is scheduled for {appointmentLetter.appointment_date} · {appointmentLetter.appointment_slot}.</p><p>{appointmentLetter.package}</p><Button variant="outline" onClick={() => downloadLetter(appointmentLetter.booking_id)} data-testid="download-appointment-letter-button"><Download size={17} />Download PDF</Button></div><button className="text-button" type="button" onClick={() => setAppointmentLetter(null)} data-testid="close-appointment-letter-button">Close</button></section>}
      {showWizard ? <BookingWizard onComplete={completeBookings} /> : <Button onClick={() => setShowWizard(true)} data-testid="start-health-check-button"><Plus size={18} />Book a family health check</Button>}
      <section className="booking-history" data-testid="booking-history-section">
        <div className="section-heading"><div><p className="eyebrow">Appointments</p><h2>Your bookings</h2></div></div>
        <div className="status-tabs" role="tablist" aria-label="Filter bookings by status" data-testid="booking-status-tabs">
          {TABS.map((tab) => {
            const count = appointments.filter(tab.matches).length;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={statusTab === tab.value}
                className={statusTab === tab.value ? "is-selected" : ""}
                onClick={() => setStatusTab(tab.value)}
                data-testid={`booking-status-tab-${tab.value}`}
              >
                {tab.label}<span className="status-tabs__count">{count}</span>
              </button>
            );
          })}
        </div>
        {visibleAppointments.length ? visibleAppointments.map((item) => item.kind === "lab_order" ? (
          <article className="booking-card" key={`lab-${item.id}`} data-testid={`lab-order-card-${item.id}`}>
            <div>
              <span className="status-pill" data-testid={`lab-order-status-${item.id}`}>{item.status.replaceAll("_", " ")}</span>
              <h3>{item.record.items?.length || 0} lab test{(item.record.items?.length || 0) === 1 ? "" : "s"} · home collection</h3>
              <p>{item.record.patient?.name} · {item.record.collection_date} · {item.record.collection_slot}</p>
            </div>
            {/* Only a paid order has a letter to show; an unpaid one has
                nothing to confirm yet. */}
            {item.status === "confirmed" && (
              <button className="icon-button icon-button--outlined" type="button" onClick={() => downloadFile(`/lab-tests/orders/${item.id}/letter`, `collection-letter-${item.id}.pdf`, token)} aria-label="Download collection letter" data-testid={`lab-order-letter-${item.id}-button`}>
                <Download size={18} />
              </button>
            )}
          </article>
        ) : (
          <article className="booking-card" key={item.id} data-testid={`booking-card-${item.id}`}>
            <div>
              <span className="status-pill" data-testid={`booking-status-${item.id}`}>{item.status.replaceAll("_", " ")}</span>
              <h3>{item.record.package_name}</h3>
              <p>{item.record.beneficiary_name} · {item.record.appointment_date} · {item.record.appointment_slot}</p>
              <AppointmentActions booking={item.record} onSubmitted={recordChangeRequest} />
            </div>
            <button className="icon-button icon-button--outlined" type="button" onClick={() => openLetter(item.id)} aria-label="View appointment letter" data-testid={`appointment-letter-${item.id}-button`}>
              <Download size={18} />
            </button>
          </article>
        )) : <p className="empty-state" data-testid="empty-booking-history">{statusTab === "all" ? "Your booked health checks and lab tests will be shown here." : `Nothing ${activeTab.label.toLowerCase()}.`}</p>}
      </section>
    </section>
  );
}
